import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DollarSign, Save, Info, User, Briefcase, Crown } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommissionSettings({ token }) {
  const [settings, setSettings] = useState({
    rep_commission_type: "fixed", rep_commission_value: 50,
    manager_override_type: "fixed", manager_override_value: 10,
    director_override_type: "fixed", director_override_value: 5
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/admin/commissions`, { headers });
        setSettings(res.data);
      } catch { toast.error("Failed to load"); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/commissions`, settings, { headers });
      toast.success("Commission settings saved");
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-center text-white/50 py-12">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="commission-settings">
      <div className="card p-6 border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/5 to-transparent">
        <div className="flex items-center gap-3 mb-6">
          <DollarSign className="w-5 h-5 text-[#D4AF37]" />
          <h3 className="font-['Playfair_Display'] text-lg font-bold text-white">Commission Structure</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Rep Commission */}
          <div className="p-4 bg-black/30 border border-green-500/20">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-green-400" />
              <h4 className="font-semibold text-white">Sales Rep Commission</h4>
            </div>
            <div className="space-y-3">
              <Select 
                value={settings.rep_commission_type} 
                onValueChange={(v) => setSettings({...settings, rep_commission_type: v})}
              >
                <SelectTrigger className="input-dark" data-testid="rep-commission-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="fixed">Fixed ($)</SelectItem>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                </SelectContent>
              </Select>
              <Input 
                type="number" 
                value={settings.rep_commission_value} 
                onChange={(e) => setSettings({...settings, rep_commission_value: parseFloat(e.target.value) || 0})} 
                className="input-dark"
                data-testid="rep-commission-value"
              />
            </div>
          </div>

          {/* Manager Override */}
          <div className="p-4 bg-black/30 border border-blue-500/20">
            <div className="flex items-center gap-2 mb-4">
              <Briefcase className="w-5 h-5 text-blue-400" />
              <h4 className="font-semibold text-white">Manager Override</h4>
            </div>
            <div className="space-y-3">
              <Select 
                value={settings.manager_override_type} 
                onValueChange={(v) => setSettings({...settings, manager_override_type: v})}
              >
                <SelectTrigger className="input-dark" data-testid="manager-commission-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="fixed">Fixed ($)</SelectItem>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                </SelectContent>
              </Select>
              <Input 
                type="number" 
                value={settings.manager_override_value} 
                onChange={(e) => setSettings({...settings, manager_override_value: parseFloat(e.target.value) || 0})} 
                className="input-dark"
                data-testid="manager-commission-value"
              />
            </div>
          </div>

          {/* Director Override */}
          <div className="p-4 bg-black/30 border border-purple-500/20">
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-5 h-5 text-purple-400" />
              <h4 className="font-semibold text-white">Director Override</h4>
            </div>
            <div className="space-y-3">
              <Select 
                value={settings.director_override_type} 
                onValueChange={(v) => setSettings({...settings, director_override_type: v})}
              >
                <SelectTrigger className="input-dark" data-testid="director-commission-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="fixed">Fixed ($)</SelectItem>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                </SelectContent>
              </Select>
              <Input 
                type="number" 
                value={settings.director_override_value} 
                onChange={(e) => setSettings({...settings, director_override_value: parseFloat(e.target.value) || 0})} 
                className="input-dark"
                data-testid="director-commission-value"
              />
            </div>
          </div>
        </div>

        <Button 
          onClick={saveSettings} 
          disabled={saving} 
          className="btn-primary mt-6 flex items-center gap-2"
          data-testid="save-commission-btn"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Commission Settings"}
        </Button>
      </div>

      <div className="card p-6 border border-white/10">
        <div className="flex items-center gap-3 mb-4">
          <Info className="w-5 h-5 text-[#00D4FF]" />
          <h3 className="font-semibold text-white">How Commissions Work</h3>
        </div>
        <div className="text-white/60 text-sm space-y-2">
          <p><span className="text-green-400">Sales Rep</span> earns base commission on each sale they refer</p>
          <p><span className="text-blue-400">Manager</span> earns override on sales from their assigned reps</p>
          <p><span className="text-purple-400">Director</span> earns override on all sales in their hierarchy</p>
          <p>Product-specific commission overrides take priority when set</p>
        </div>
      </div>
    </div>
  );
}
